import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { IconButton, PrimaryButton, SecondaryButton } from '../../components/Button'
import Modal, { ModalProps, ModalFooter } from '../../components/Modal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInput } from '../../components/Input'
import { ManagedPaymentMethod, getMethod, getPaymentMethods } from '../../util/Managed'
import { formatWithdrawalAccount } from '../../components/Shared'
import User from '../../state/User'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, shortAddress, successToast, useTestMode } from '../../util/Util'
import { ManagedPaymentMethodSelector } from '../../components/ManagedMethodMultiselector'
import Radom, { WithdrawalAccount } from '../../state/Radom'
import Close from '../../icons/Close'
import WithdrawalTransactionIcon from '../../icons/WithdrawalTransactionIcon'
import SwapTransactionIcon from '../../icons/SwapTransactionIcon'
import NumberInput from '../../components/NumberInput'
import { useNavigate } from 'react-router-dom'
import SearchMultiselect from '../../components/SearchMultiselect'
import { usePermissions } from '../../util/permissions'
import { ISwapSupportedAsset, isSwapSupportedAsset } from '../../util/OkxSwap'
import { uniq } from 'lodash'
import { AssetInline, NetworkIcon } from '../../components/AssetIdentity'
import { assetLineFor, networkName } from '../../util/AssetLabels'

interface TargetCandidate {
  key: string
  label: string
  network: string
  token: string | null
  method: ManagedPaymentMethod
}

enum TriggerType {
  MinimumAmount,
  TimeInterval,
  WithdrawalAmount
}

interface Trigger {
  triggerType: TriggerType
  triggerName: string
  placeholder: string
  value: string
}

const TriggerTypes = [
  {
    triggerType: TriggerType.MinimumAmount,
    triggerName: 'Minimum amount',
    placeholder: 'Enter amount (USD)',
    value: ''
  },
  {
    triggerType: TriggerType.TimeInterval,
    triggerName: 'Time interval',
    placeholder: 'Select time interval',
    value: ''
  },
  {
    triggerType: TriggerType.WithdrawalAmount,
    triggerName: 'Amount',
    placeholder: 'Enter amount (USD)',
    value: ''
  }
]

const getAvailableTriggerTypes = (triggers: Array<Trigger | null>): typeof TriggerTypes => {
  const selectedTypes = triggers.filter((t): t is Trigger => t !== null).map(t => t.triggerType)
  const hasMinimumAmount = selectedTypes.includes(TriggerType.MinimumAmount)
  const hasTimeInterval = selectedTypes.includes(TriggerType.TimeInterval)
  const hasWithdrawalAmount = selectedTypes.includes(TriggerType.WithdrawalAmount)

  return TriggerTypes.filter(t => {
    if (selectedTypes.includes(t.triggerType)) {
      return false
    }
    if (t.triggerType === TriggerType.WithdrawalAmount && hasMinimumAmount) {
      return false
    }
    if (t.triggerType === TriggerType.MinimumAmount && (hasWithdrawalAmount || hasTimeInterval)) {
      return false
    }
    if (t.triggerType === TriggerType.TimeInterval && hasMinimumAmount) {
      return false
    }
    return true
  })
}

const isValidTriggerCombination = (triggers: Array<Trigger | null>): boolean => {
  const selected = triggers.filter((t): t is Trigger => t !== null)
  if (selected.length === 0) {
    return false
  }

  const types = selected.map(t => t.triggerType)
  const hasWithdrawalAmount = types.includes(TriggerType.WithdrawalAmount)
  const hasTimeInterval = types.includes(TriggerType.TimeInterval)

  if (hasWithdrawalAmount) {
    return hasTimeInterval && selected.length === 2
  }

  return selected.every(t => t.value !== '')
}

const ETHEREUM_USDC_TOKEN = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'
const SEPOLIA_USDC_TOKEN = '0xa4fce8264370437e718ae207805b4e6233638b9e'

type CashWithdrawalCurrency = 'EUR' | 'USD'

const getFiatAccountCurrency = (account: WithdrawalAccount): CashWithdrawalCurrency | undefined => {
  if (account.accountDetails.Fiat?.accountType.iban) {
    return 'EUR'
  }
  if (account.accountDetails.Fiat?.accountType.us) {
    return 'USD'
  }
}

const isFiatFundingAsset = (
  method: ManagedPaymentMethod,
  fiatAccount: WithdrawalAccount,
  testMode: boolean,
  usesLegacyEurOfframp: boolean
): boolean => {
  const fiatCurrency = getFiatAccountCurrency(fiatAccount)
  if (!fiatCurrency) {
    return false
  }

  const network = method.hostChain?.name ?? method.name
  const token = method.hostChain?.tokenAddress
  const assetMethod = getMethod(network, token)
  const tokenAddress = token?.toLowerCase()
  const expectedUsdcNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'
  const expectedUsdcToken = (testMode ? SEPOLIA_USDC_TOKEN : ETHEREUM_USDC_TOKEN).toLowerCase()

  if (fiatCurrency === 'USD') {
    return ['USDC', 'USDT', 'EURC'].includes(assetMethod.ticker)
  }

  if (usesLegacyEurOfframp) {
    return ['USDC', 'USDT', 'EURC'].includes(assetMethod.ticker)
  }

  return network === expectedUsdcNetwork &&
    (assetMethod.ticker === 'USDC' || tokenAddress === expectedUsdcToken)
}

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 560px;
  max-width: 100%;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const SegmentedControl = styled.div`
  background: var(--surface-hover, #f1f3f7);
  border-radius: 11px;
  display: grid;
  gap: 3px;
  grid-template-columns: 1fr 1fr;
  padding: 3px;
`

const Segment = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  border: 0;
  border-radius: 9px;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST};
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  font-size: 13.5px;
  font-weight: 500;
  gap: 8px;
  justify-content: center;
  min-height: 38px;
  transition: 0.15s ease all;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:not(:disabled):hover {
    color: ${RADOM_COLORS.BLACK};
  }
`

const FieldHint = styled.span`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 12.5px;
  line-height: 1.45;
`

const ConditionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`

const ConditionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;

  @media (max-width: 520px) {
    flex-wrap: wrap;
  }
`

const ConditionConnector = styled.span`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  padding-left: 2px;
`

const AddConditionButton = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  gap: 7px;
  padding: 2px 0;
  transition: 0.15s ease opacity;
  width: fit-content;

  &:disabled {
    color: ${RADOM_COLORS.GRAY_DARKER};
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.65;
  }
`

const Recap = styled.p`
  border-left: 2px solid var(--border, #e2e4ef);
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 12.75px;
  line-height: 1.55;
  margin: 0;
  padding-left: 12px;
`

const RecapStrong = styled.span`
  color: ${RADOM_COLORS.BLACK};
  font-weight: 500;
`

const NoteBox = styled.div`
  background: var(--surface-subtle, #f8f8fb);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 13px 14px;
`

const NoteBullet = styled.div`
  align-items: flex-start;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  display: flex;
  font-size: 12.5px;
  gap: 10px;
  line-height: 1.5;

  &::before {
    background: ${RADOM_COLORS.GRAY_DARKER};
    border-radius: 50%;
    content: '';
    flex-shrink: 0;
    height: 4px;
    margin-top: 7px;
    width: 4px;
  }
`

type Props = ModalProps & {
  onAutomationCreated: () => void
  initialAutomationType?: 'withdrawal' | 'autoswap'
  /** Tickers that already have a withdrawal rule on a pooled organization; one rule per asset. */
  coveredAssets?: string[]
}

const AutomationModal = observer(({ onAutomationCreated, initialAutomationType, coveredAssets = [], ...props }: Props): ReactElement => {
  const navigate = useNavigate()

  const [testMode] = useTestMode()

  const [automationName, setAutomationName] = useState('')
  const [automationType, setAutomationType] = useState<'withdrawal' | 'autoswap'>(initialAutomationType ?? 'withdrawal')
  const [withdrawalAccount, setWithdrawalAccount] = useState<WithdrawalAccount>()
  const [selectedMethods, setSelectedMethods] = useState<ManagedPaymentMethod[]>([])
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const canReadWithdrawalAccounts = has('ReadWithdrawalAccount')

  const [isCreatingAutomation, setIsCreatingAutomation] = useState(false)
  const usesLegacyEurOfframp = User.organization?.legacyEurOfframpEnabled === true

  const selectedAssetNetwork = selectedMethods[0]
    ? (selectedMethods[0].hostChain?.name ?? selectedMethods[0].name)
    : undefined
  const cryptoAllowedNetworks = withdrawalAccount?.accountDetails.Fiat || !selectedAssetNetwork
    ? undefined
    : [selectedAssetNetwork]

  useEffect(() => {
    if (selectedMethods.length === 0) {
      setWithdrawalAccount(undefined)
      return
    }

    const selectedMethod = selectedMethods[0]
    setWithdrawalAccount(current => {
      if (!current) {
        return current
      }
      if (current.accountDetails.Fiat) {
        return isFiatFundingAsset(selectedMethod, current, testMode, usesLegacyEurOfframp) ? current : undefined
      }
      const network = selectedMethod.hostChain?.name ?? selectedMethod.name
      if (current.accountDetails.Crypto?.network !== network) {
        return undefined
      }
      return current
    })
  }, [selectedMethods, testMode, usesLegacyEurOfframp])

  useEffect(() => {
    if (!withdrawalAccount?.accountDetails.Fiat || selectedMethods.length === 0) {
      return
    }
    if (!isFiatFundingAsset(selectedMethods[0], withdrawalAccount, testMode, usesLegacyEurOfframp)) {
      setSelectedMethods([])
    }
  }, [withdrawalAccount, selectedMethods, testMode, usesLegacyEurOfframp])

  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])

  const filteredWithdrawalAccounts = withdrawalAccounts.filter(w => {
    if (w.accountDetails.Crypto) {
      const method = getMethod(w.accountDetails.Crypto.network)
      if (testMode ? !method.isTestnet : method.isTestnet) {
        return false
      }
      return cryptoAllowedNetworks === undefined ||
        cryptoAllowedNetworks.includes(w.accountDetails.Crypto.network)
    }

    if (w.accountDetails.Fiat) {
      return false
    }

    return false
  })

  const paymentMethodFilter = withdrawalAccount?.accountDetails.Fiat
    ? (method: ManagedPaymentMethod) => isFiatFundingAsset(method, withdrawalAccount, testMode, usesLegacyEurOfframp)
    : undefined
  // Pooled organizations hold one balance per asset, so an asset that already
  // has a rule cannot be picked again; the API refuses it as well.
  const coveredTickers = new Set(coveredAssets)
  const selectableMethod = (method: ManagedPaymentMethod): boolean =>
    (!paymentMethodFilter || paymentMethodFilter(method)) && !coveredTickers.has(method.ticker)

  const [triggers, setTriggers] = useState<Array<Trigger | null>>([
    null
  ])

  useEffect(() => {
    if (!permissionsLoaded || !canReadWithdrawalAccounts) {
      return
    }

    Radom
      .listWithdrawalAccounts()
      .then(a => setWithdrawalAccounts(a))
      .catch(err => console.error('Failed to load withdrawal accounts', err))
  }, [permissionsLoaded, canReadWithdrawalAccounts])

  const [selectedSwapTarget, setSelectedSwapTarget] = useState<TargetCandidate | undefined>()
  const [okxSwapAssets, setOkxSwapAssets] = useState<ISwapSupportedAsset[]>([])
  const [hasLoadedSwapAssets, setHasLoadedSwapAssets] = useState(false)
  const [isLoadingSwapAssets, setIsLoadingSwapAssets] = useState(false)

  const isSwapFeatureEnabled = User.organization?.features?.isConversionEnabled === true
  const unifiedTreasury = User.organization?.features?.isUnifiedTreasuryEnabled === true

  const swapCandidates = useMemo(
    () => {
      const methods = getPaymentMethods()
      const expanded = methods
        .filter((m) => m.isSwapEnabled && !m.isTestnet && !m.isFiat && !m.isHidden && !m.isDisabled)
        .filter((m) => {
          if (!hasLoadedSwapAssets) {
            return false
          }
          const network = m.hostChain?.name ?? m.name
          const token = m.hostChain?.tokenAddress ?? null
          return isSwapSupportedAsset(network, token, okxSwapAssets)
        })
        .map((m) => {
          const network = m.hostChain?.name ?? m.name
          const token = m.hostChain?.tokenAddress ?? null
          return {
            key: `${network}:${token ?? ''}`,
            label: assetLineFor(m.ticker, unifiedTreasury ? undefined : network),
            network,
            token,
            method: m
          }
        })

      if (!unifiedTreasury) return expanded
      return expanded.filter((candidate, index, all) =>
        all.findIndex((other) => other.method.ticker === candidate.method.ticker) === index)
    },
    [hasLoadedSwapAssets, okxSwapAssets, unifiedTreasury]
  )

  useEffect(() => {
    if (!props.visible) {
      return
    }

    if (!permissionsLoaded) {
      return
    }

    setIsLoadingSwapAssets(true)
    setHasLoadedSwapAssets(false)
    setOkxSwapAssets([])

    Radom.getSwapSupportedAssets()
      .then(async (response) => {
        setOkxSwapAssets(response.assets)
        setHasLoadedSwapAssets(true)
        return await Radom.getAutoSwapOnPayment()
      })
      .then((s) => {
        if (s?.enabled && s.targetNetwork) {
          const methods = getPaymentMethods()
          const matchedMethod = methods.find(m => {
            const network = m.hostChain?.name ?? m.name
            const token = m.hostChain?.tokenAddress ?? null
            return network === s.targetNetwork && (token ?? null) === (s.targetToken ?? null)
          })
          if (matchedMethod) {
            const network = matchedMethod.hostChain?.name ?? matchedMethod.name
            const token = matchedMethod.hostChain?.tokenAddress ?? null
            setSelectedSwapTarget({
              key: `${network}:${token ?? ''}`,
              label: assetLineFor(matchedMethod.ticker, unifiedTreasury ? undefined : network),
              network,
              token,
              method: matchedMethod
            })
          }
        } else {
          setSelectedSwapTarget(undefined)
        }
      })
      .catch((err: any) => {
        setHasLoadedSwapAssets(false)
        setOkxSwapAssets([])
        console.error('Failed to load auto-swap settings', err)
      })
      .finally(() => {
        setIsLoadingSwapAssets(false)
      })
  }, [props.visible, permissionsLoaded])

  const handleSave = (): void => {
    if (automationType === 'autoswap') {
      if (!selectedSwapTarget) {
        return
      }
      setIsCreatingAutomation(true)
      Radom.updateAutoSwapOnPayment({
        enabled: true,
        targetNetwork: selectedSwapTarget.network,
        targetToken: selectedSwapTarget.token
      })
        .then(() => {
          successToast('Auto-convert settings updated')
          onAutomationCreated()
          props.onClose()
        })
        .catch(err => errorToast(err.response?.data?.error || err.message))
        .finally(() => setIsCreatingAutomation(false))
    } else {
      createAutomation()
    }
  }

  const createAutomation = (): void => {
    setIsCreatingAutomation(true)

    const req = {
      name: automationName,
      automationDetails: {
        Withdrawal: {
          assets: selectedMethods.map(m => ({
            network: m.hostChain?.name || m.name,
            token: m.hostChain?.tokenAddress
          })),
          triggers: triggers
            .filter(t => !!t)
            .map((t: Trigger) => {
              switch (t.triggerType) {
              case TriggerType.MinimumAmount:
                return {
                  minimumAmount: {
                    amount: t.value,
                    currency: 'USD'

                  }
                }
              case TriggerType.TimeInterval:
                return {
                  timeInterval: {
                    interval: t.value.toLowerCase()
                  }
                }
              case TriggerType.WithdrawalAmount:
                return {
                  withdrawalAmount: {
                    amount: t.value,
                    currency: 'USD'
                  }
                }
              default:
                return null
              }
            }),
          withdrawalAccountId: withdrawalAccount?.id
        }
      }
    }

    Radom.createAutomation(req)
      .then(() => {
        successToast('Automation created')
        onAutomationCreated()
        props.onClose()
      })
      .catch(err => errorToast(err.response?.data?.error || err.message))
      .finally(() => setIsCreatingAutomation(false))
  }

  const describeTriggers = (): string => {
    const selected = triggers.filter((t): t is Trigger => t !== null)
    const minimum = selected.find(t => t.triggerType === TriggerType.MinimumAmount)
    const time = selected.find(t => t.triggerType === TriggerType.TimeInterval)
    const amount = selected.find(t => t.triggerType === TriggerType.WithdrawalAmount)
    const cadence = (time?.value || 'scheduled').toLowerCase()

    if (amount && time) {
      return `withdrawing $${amount.value || '0'} ${cadence}`
    }
    if (minimum) {
      return `when the balance rises above $${minimum.value || '0'}`
    }
    if (time) {
      return `on a ${cadence} schedule`
    }
    return ''
  }

  const recapAssets = uniq(selectedMethods.map(m => m.ticker)).join(', ')
  const recapTrigger = describeTriggers()
  const showRecap = automationType === 'withdrawal' &&
    !!withdrawalAccount &&
    isValidTriggerCombination(triggers)

  // Options for a single row are derived from the *other* rows, with the row's
  // own current type always included — otherwise a fully-configured combination
  // (e.g. Amount + Time interval) leaves each row's dropdown empty.
  const getTriggerOptionsForRow = (i: number): typeof TriggerTypes => {
    const otherTriggers = triggers.filter((_, idx) => idx !== i)
    const available = getAvailableTriggerTypes(otherTriggers)
    const current = triggers[i]

    const merged = current && !available.some(o => o.triggerType === current.triggerType)
      ? [...available, ...TriggerTypes.filter(t => t.triggerType === current.triggerType)]
      : available

    return TriggerTypes.filter(t => merged.some(m => m.triggerType === t.triggerType))
  }

  const renderTriggerRow = (t: Trigger | null, i: number): ReactElement => {
    const onRemove = (): void => {
      if (t === null) {
        setTriggers([...triggers.slice(0, i), ...triggers.slice(i + 1)])
        return
      }
      if (triggers.length === 1) {
        setTriggers([null])
        return
      }
      setTriggers([...triggers.slice(0, i), ...triggers.slice(i + 1)])
    }

    const canRemove = t !== null || triggers.length > 1

    const typeDropdown = (matchWidth: boolean): ReactElement =>
      <Dropdown
        disabled={isCreatingAutomation}
        matchTriggerWidth={matchWidth}
        containerStyle={{ width: '100%' }}
        selectedContent={
          <span style={{ fontSize: 14, color: t ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKER }}>
            {t ? t.triggerName : 'Select condition'}
          </span>
        }
        dropdownContent={
          getTriggerOptionsForRow(i).map(option =>
            <DropdownItem
              key={option.triggerName}
              onClick={() => {
                const newTriggers = [...triggers]
                newTriggers[i] = { ...option, value: t?.triggerType === option.triggerType ? t.value : '' }
                setTriggers(newTriggers)
              }}>
              {option.triggerName}
            </DropdownItem>
          )
        } />

    const removeButton = canRemove &&
      <IconButton
        disabled={isCreatingAutomation}
        style={{ padding: 9, flexShrink: 0 }}
        onClick={onRemove}>
        <Close style={{ width: 13 }} />
      </IconButton>

    if (t === null) {
      return <ConditionRow>
        <div style={{ flex: 1, minWidth: 0 }}>{typeDropdown(false)}</div>
        {removeButton}
      </ConditionRow>
    }

    return <ConditionRow>
      <div style={{ flex: '0 0 170px', minWidth: 0 }}>{typeDropdown(true)}</div>

      <div style={{ display: 'flex', flex: '1 1 auto', minWidth: 120 }}>
        {(t.triggerType === TriggerType.MinimumAmount || t.triggerType === TriggerType.WithdrawalAmount) &&
          <NumberInput
            disabled={isCreatingAutomation}
            placeholder={t.placeholder}
            fontSize='14'
            value={t.value}
            onUserInput={(v) => {
              const newTriggers = [...triggers]
              newTriggers[i] = { ...t, value: v }
              setTriggers(newTriggers)
            }} />}

        {t.triggerType === TriggerType.TimeInterval &&
          <Dropdown
            key={t.value}
            outerStyle={{ flexGrow: 1 }}
            containerStyle={{ width: '100%' }}
            disabled={isCreatingAutomation}
            selectedContent={
              <span style={{ fontSize: 14, color: t.value ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKER }}>
                {t.value ? t.value : t.placeholder}
              </span>
            }
            dropdownContent={
              ['Daily', 'Weekly', 'Monthly'].map(value =>
                <DropdownItem
                  key={value}
                  onClick={() => {
                    const newTriggers = [...triggers]
                    newTriggers[i] = { ...t, value }
                    setTriggers(newTriggers)
                  }}>
                  {value}
                </DropdownItem>
              )
            } />}
      </div>

      {removeButton}
    </ConditionRow>
  }

  const canAddTrigger = !(
    isCreatingAutomation ||
    triggers[triggers.length - 1] === null ||
    triggers.length === 2 ||
    triggers.some(t => t?.triggerType === TriggerType.MinimumAmount) ||
    (triggers.some(t => t?.triggerType === TriggerType.WithdrawalAmount) &&
      triggers.some(t => t?.triggerType === TriggerType.TimeInterval))
  )

  return <Modal presentation='drawer' title='Create automation' {...props}>
    <FormColumn>
      <Field>
        <SegmentedControl>
          <Segment
            type='button'
            $active={automationType === 'withdrawal'}
            disabled={isCreatingAutomation}
            onClick={() => { if (!isCreatingAutomation) setAutomationType('withdrawal') }}>
            <WithdrawalTransactionIcon style={{ width: 16, height: 16 }} />
            Withdraw to your account
          </Segment>
          <Segment
            type='button'
            $active={automationType === 'autoswap'}
            disabled={!isSwapFeatureEnabled || isCreatingAutomation}
            onClick={() => { if (!isCreatingAutomation && isSwapFeatureEnabled) setAutomationType('autoswap') }}>
            <SwapTransactionIcon style={{ width: 16, height: 16 }} />
            Auto-convert
          </Segment>
        </SegmentedControl>
        <FieldHint>
          {automationType === 'withdrawal'
            ? 'Move funds to one of your accounts on a schedule, or whenever a balance passes a threshold.'
            : isSwapFeatureEnabled
              ? 'Convert every incoming payment or deposit into the currency you choose.'
              : 'Auto-convert is not enabled for your organization.'}
        </FieldHint>
      </Field>

      {automationType === 'withdrawal' && (
        <>
          <Field>
            <InputLabel>Name</InputLabel>
            <TextInput
              disabled={isCreatingAutomation}
              placeholder='Give this automation a name'
              required
              value={automationName}
              onChange={(e) => setAutomationName((e.target as HTMLInputElement).value)} />
          </Field>

          <Field>
            <ManagedPaymentMethodSelector
              disabled={isCreatingAutomation}
              label='Asset'
              placeholderText='Select asset'
              selectedMethods={selectedMethods}
              setSelectedMethods={setSelectedMethods}
              allowedNetworks={cryptoAllowedNetworks}
              filterMethod={selectableMethod}
              testnetOnly={testMode} />
            {coveredAssets.length > 0 && <FieldHint>
              Already covered by your other rules: {coveredAssets.join(', ')}. Each asset has one rule; delete a rule to change where that asset goes.
            </FieldHint>}
          </Field>

          <Field>
            <InputLabel>Trigger</InputLabel>

            <ConditionList>
              {triggers.map((t, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ConditionConnector>AND</ConditionConnector>}
                  {renderTriggerRow(t, i)}
                </React.Fragment>
              ))}
            </ConditionList>

            {canAddTrigger &&
              <AddConditionButton
                type='button'
                onClick={() => setTriggers([...triggers, null])}>
                <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
                <span>Add condition</span>
              </AddConditionButton>}
          </Field>

          <Field>
            <InputLabel>Your account</InputLabel>
            {
              !canReadWithdrawalAccounts &&
              <FieldHint>
                Your role can’t view your accounts, so you can’t choose where this automation sends funds.
              </FieldHint>
            }
            {
              canReadWithdrawalAccounts &&
              filteredWithdrawalAccounts.length === 0 &&
              <SecondaryButton
                onClick={() => {
                  navigate('/accounts')
                  props.onClose()
                }}>
                Add one of your accounts
              </SecondaryButton>
            }
            {
              filteredWithdrawalAccounts.length > 0 &&
              <Dropdown
                disabled={isCreatingAutomation}
                matchTriggerWidth
                selectedContent={
                  withdrawalAccount
                    ? <div
                      style={{
                        fontSize: 14,
                        alignItems: 'center',
                        display: 'flex',
                        gap: 10
                      }}>
                      {
                        withdrawalAccount.accountDetails.Crypto &&
                        <NetworkIcon
                          method={getMethod(withdrawalAccount.accountDetails.Crypto.network)}
                          size={20} />
                      }
                      <span>{withdrawalAccount.name}</span>
                      {
                        withdrawalAccount.accountDetails.Crypto &&
                        <span style={{ opacity: 0.5, fontSize: 12 }}>
                          {networkName(withdrawalAccount.accountDetails.Crypto.network)}
                        </span>
                      }
                      {
                        withdrawalAccount.accountDetails.Fiat &&
                        <span style={{ opacity: 0.5, fontSize: 12 }}>
                          {formatWithdrawalAccount(withdrawalAccount.accountDetails)}
                        </span>
                      }
                    </div>
                    : <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKER }}>Select account</span>
                }
                dropdownContent={
                  filteredWithdrawalAccounts
                    .filter(t => t.id !== withdrawalAccount?.id)
                    .map(t =>
                      <DropdownItem
                        key={t.name}
                        onClick={() => setWithdrawalAccount(t)}
                        style={{
                          fontSize: 14,
                          alignItems: 'center',
                          display: 'flex',
                          gap: 10
                        }}>
                        {
                          t.accountDetails.Crypto &&
                          <NetworkIcon method={getMethod(t.accountDetails.Crypto.network)} size={20} />
                        }
                        <span>{t.name}</span>
                        {
                          t.accountDetails.Crypto &&
                          <span style={{ opacity: 0.5, fontSize: 12 }}>
                            {networkName(t.accountDetails.Crypto.network)}
                          </span>
                        }
                        {
                          t.accountDetails.Crypto?.address &&
                          <span style={{ opacity: 0.3 }}>{shortAddress(t.accountDetails.Crypto.address)}</span>
                        }
                        {
                          t.accountDetails.Fiat &&
                          <span style={{ opacity: 0.3, fontSize: 12 }}>
                            {formatWithdrawalAccount(t.accountDetails)}
                          </span>
                        }

                      </DropdownItem>
                    )
                } />
            }
          </Field>

          {showRecap &&
            <Recap>
              Automatically move <RecapStrong>{recapAssets || 'balances'}</RecapStrong>{' '}
              to <RecapStrong>{withdrawalAccount?.name}</RecapStrong>
              {recapTrigger && <>, <RecapStrong>{recapTrigger}</RecapStrong></>}.
            </Recap>}
        </>
      )}

      {automationType === 'autoswap' && (
        <>
          <NoteBox>
            <NoteBullet>
              Every incoming payment or deposit is automatically converted to your chosen currency
            </NoteBullet>
            <NoteBullet>
              Swaps run in the background. Balances are credited normally if anything goes wrong
            </NoteBullet>
            <NoteBullet>
              Payment receipts are never blocked on the swap completing
            </NoteBullet>
          </NoteBox>

          <Field>
            <InputLabel>Target currency</InputLabel>
            <div style={{ minWidth: 280 }}>
              <SearchMultiselect
                isLoading={isLoadingSwapAssets}
                disabled={isCreatingAutomation}
                placeholder="Select currency"
                itemArray={swapCandidates}
                selectedItems={selectedSwapTarget ? [selectedSwapTarget] : []}
                setSelectedItems={(items) => setSelectedSwapTarget(items[0])}
                keyExtractor={(c: TargetCandidate) => c.key}
                labelExtractor={(c: TargetCandidate) =>
                  <AssetInline method={c.method} network={!unifiedTreasury} />}
                isSingleSelect
              />
            </div>
          </Field>
        </>
      )}
    </FormColumn>

    <ModalFooter $fullWidthAction>
      <PrimaryButton
        isLoading={isCreatingAutomation}
        disabled={
          automationType === 'autoswap'
            ? !selectedSwapTarget
            : (filteredWithdrawalAccounts.length === 0 ||
                !withdrawalAccount ||
                !isValidTriggerCombination(triggers))
        }
        style={{ width: '100%' }}
        onClick={handleSave}>
        {automationType === 'autoswap' ? 'Save settings' : 'Create automation'}
      </PrimaryButton>
    </ModalFooter>
  </Modal>
})

export default AutomationModal
